import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaTimes } from 'react-icons/fa';

const projects = [
  {
    image: '/images/project/Jardim vertical natural.jpg',
    category: 'vertical',
    title: 'Jardim Vertical Natural',
  },
  {
    image: '/images/project/Jardim vertical natural(1).jpg',
    category: 'vertical',
    title: 'Jardim Vertical Natural',
  },
  {
    image: '/images/project/Jardim-artificial-.jpg',
    category: 'vertical',
    title: 'Jardim Vertical Artificial',
  },
  {
    image: '/images/project/Jardim-preservado.jpg',
    category: 'vertical',
    title: 'Jardim Vertical Preservado',
  },
  {
    image: '/images/project/Árvore preservada.jpg',
    category: 'permanente',
    title: 'Árvore Preservada',
  },
  {
    image: '/images/project/Árvores preservadas .jpg',
    category: 'permanente',
    title: 'Árvores Preservadas',
  },
  {
    image: '/images/project/Paisagismo natural.jpg',
    category: 'natural',
    title: 'Paisagismo Natural',
  },
  {
    image: '/images/project/Paisagismo natural.PNG',
    category: 'natural',
    title: 'Paisagismo Natural',
  },
];

type Category = 'all' | 'vertical' | 'permanente' | 'natural';

const GallerySection = () => {
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'vertical', label: 'Jardins Verticais' },
    { id: 'permanente', label: 'Paisagismo Permanente' },
    { id: 'natural', label: 'Paisagismo Natural' },
  ];

  const filteredProjects = projects.filter(
    (project) => selectedCategory === 'all' || project.category === selectedCategory
  );

  return (
    <section className="section-padding bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-primary-500 font-semibold text-sm uppercase tracking-wider">
            Projetos
          </span>
          <h2 className="text-dark mt-4 mb-6">
            Nossos <span className="text-primary-500">trabalhos recentes</span>
          </h2>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setSelectedCategory(category.id as Category)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.label}
            </motion.button>
          ))}
        </div>

        {/* Gallery Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence>
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.image}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group relative aspect-square rounded-xl overflow-hidden shadow-lg hover:shadow-2xl cursor-pointer"
                onClick={() => setSelectedImage(project.image)}
                whileHover={{ y: -8 }}
              >
                {/* Image */}
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <h4 className="text-white font-bold text-lg mb-2">{project.title}</h4>
                    <span className="inline-block bg-primary-500 text-white text-xs px-3 py-1 rounded-full">
                      Ver detalhes
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-pointer"
          >
            <motion.button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-3 rounded-full backdrop-blur-sm transition-colors z-10"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaTimes size={24} />
            </motion.button>

            <motion.img
              src={selectedImage}
              alt="Projeto em destaque"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySection;
